import { useEffect, useState } from "react";
import { Copy, PackageSearch, Search, Share2 } from "lucide-react";
import { toast } from "sonner";
import { api, money } from "@/lib/api";
import Reveal from "@/components/Reveal";

const STATUS_STYLES = {
  paid: "border-voltaic-green/40 bg-voltaic-green/10 text-voltaic-green",
  pending_payment: "border-voltaic-amber/40 bg-voltaic-amber/10 text-voltaic-amber",
  shipped: "border-voltaic-cyan/40 bg-voltaic-cyan/10 text-voltaic-cyan",
};

const OrderCard = ({ order }) => {
  const paid = order.status === "paid";
  return (
    <div data-testid={`order-card-${order.order_id}`} className="rounded-2xl border border-voltaic-line bg-voltaic-surface/60 p-5 sm:p-6">
      <div className="flex flex-wrap items-center justify-between gap-3">
        <div>
          <p className="font-mono text-xs uppercase tracking-[0.2em] text-voltaic-cyan">{order.order_id}</p>
          <p className="mt-1 text-xs text-voltaic-muted">
            {new Date(order.created_at).toLocaleString("en-US", { dateStyle: "medium", timeStyle: "short" })}
          </p>
        </div>
        <span className={`rounded-full border px-3.5 py-1.5 font-mono text-[10px] uppercase tracking-[0.2em] ${STATUS_STYLES[order.status] || "border-voltaic-line text-voltaic-muted"}`}>
          {order.status === "shipped" ? "Shipped" : paid ? "Paid" : "Awaiting payment"}
        </span>
      </div>
      <div className="mt-4 space-y-2.5">
        {order.items.map((i) => (
          <div key={i.product_id} className="flex items-center gap-3">
            <img src={i.image} alt={i.name} className="h-11 w-11 rounded-lg object-cover" />
            <div className="min-w-0 flex-1">
              <p className="truncate text-sm font-semibold">{i.name}</p>
              <p className="font-mono text-[10px] uppercase tracking-[0.15em] text-voltaic-muted">Qty {i.quantity}</p>
            </div>
            <p className="text-sm font-bold">{money(i.price * i.quantity)}</p>
          </div>
        ))}
      </div>
      <div className="mt-4 flex items-center justify-between border-t border-voltaic-line pt-3">
        <span className="font-mono text-[10px] uppercase tracking-[0.2em] text-voltaic-muted">Total paid</span>
        <span className="font-display text-lg font-extrabold">{money(order.subtotal - (order.discount || 0) + (order.shipping || 0))}</span>
      </div>
    </div>
  );
};

export default function Orders() {
  const [query, setQuery] = useState("");
  const [recent, setRecent] = useState([]);
  const [results, setResults] = useState(null);
  const [referral, setReferral] = useState(null);
  const [busy, setBusy] = useState(false);
  const [error, setError] = useState("");

  useEffect(() => {
    try {
      setRecent(JSON.parse(localStorage.getItem("voltaic_orders") || "[]"));
    } catch {
      setRecent([]);
    }
  }, []);

  const lookup = async (val) => {
    const v = (val ?? query).trim();
    if (!v) return;
    setBusy(true);
    setError("");
    setResults(null);
    setReferral(null);
    try {
      let data;
      if (v.toUpperCase().startsWith("VLT-")) {
        data = [(await api.get(`/orders/${v}`)).data];
      } else if (v.includes("@")) {
        data = (await api.get("/orders", { params: { email: v } })).data;
        api.post("/referrals/code", { email: v })
          .then((r) => setReferral(r.data))
          .catch(() => {});
      } else {
        throw new Error("Enter an order ID (VLT-...) or the email you used at checkout");
      }
      setResults(data);
      if (!data.length) setError("No orders found for that search.");
    } catch (e) {
      setResults([]);
      setError(
        e?.response?.status === 404
          ? "Order not found — double-check the ID."
          : e?.message || "Lookup failed"
      );
    }
    setBusy(false);
  };

  const copyCode = async () => {
    try {
      await navigator.clipboard.writeText(referral.code);
      toast.success("Referral code copied");
    } catch {
      /* clipboard unavailable */
    }
  };

  return (
    <main className="mx-auto max-w-3xl px-5 pb-24 pt-32 lg:px-10" data-testid="orders-page">
      <Reveal>
        <p className="font-mono text-[11px] uppercase tracking-[0.3em] text-voltaic-cyan">Order tracker</p>
        <h1 className="mt-2 font-display text-3xl font-extrabold uppercase tracking-tight sm:text-4xl">
          Where's my <span className="text-outline">gear?</span>
        </h1>
        <p className="mt-3 text-sm text-voltaic-muted">Look up any order with its ID or the email used at checkout.</p>
      </Reveal>

      <Reveal delay={0.08}>
        <div className="mt-8 flex gap-2">
          <div className="relative flex-1">
            <PackageSearch size={16} className="absolute left-4 top-1/2 -translate-y-1/2 text-voltaic-muted" />
            <input
              data-testid="orders-lookup-input"
              value={query}
              onChange={(e) => setQuery(e.target.value)}
              onKeyDown={(e) => e.key === "Enter" && lookup()}
              placeholder="VLT-XXXXXXXXXX or ada@voltaic.dev"
              className="field !pl-11"
            />
          </div>
          <button
            data-testid="orders-lookup-button"
            onClick={() => lookup()}
            disabled={busy}
            className="flex items-center gap-2 rounded-full bg-voltaic-cyan px-6 font-semibold text-voltaic-bg transition-shadow hover:glow-cyan disabled:opacity-50"
          >
            <Search size={15} /> {busy ? "..." : "Track"}
          </button>
        </div>
        {error && (
          <p data-testid="orders-lookup-error" className="mt-3 text-sm text-voltaic-pink">{error}</p>
        )}
      </Reveal>

      {recent.length > 0 && results === null && (
        <Reveal delay={0.12}>
          <div className="mt-8 rounded-2xl border border-voltaic-line bg-voltaic-surface/40 p-5">
            <p className="font-mono text-[10px] uppercase tracking-[0.22em] text-voltaic-muted">Your recent orders (this device)</p>
            <div className="mt-3 flex flex-wrap gap-2">
              {recent.map((id) => (
                <button
                  key={id}
                  data-testid={`recent-order-${id}`}
                  onClick={() => { setQuery(id); lookup(id); }}
                  className="rounded-full border border-voltaic-line px-4 py-2 font-mono text-[11px] tracking-[0.15em] text-voltaic-cyan transition-colors hover:border-voltaic-cyan"
                >
                  {id}
                </button>
              ))}
            </div>
          </div>
        </Reveal>
      )}

      {referral && (
        <Reveal delay={0.1}>
          <div className="mt-8 rounded-2xl border border-voltaic-cyan/40 bg-voltaic-cyan/5 p-5 sm:p-6" data-testid="orders-referral-card">
            <p className="flex items-center gap-2 font-mono text-[10px] uppercase tracking-[0.25em] text-voltaic-cyan">
              <Share2 size={13} /> Your referral code — give 10%, get 10%
            </p>
            <div className="mt-3 flex flex-wrap items-center gap-3">
              <span className="rounded-full border border-dashed border-voltaic-cyan/60 bg-voltaic-cyan/10 px-5 py-2 font-mono text-sm font-bold tracking-[0.25em] text-voltaic-cyan">
                {referral.code}
              </span>
              <button
                data-testid="orders-referral-copy"
                onClick={copyCode}
                className="flex items-center gap-2 rounded-full border border-voltaic-line px-4 py-2 font-mono text-[10px] uppercase tracking-[0.18em] transition-colors hover:border-voltaic-cyan hover:text-voltaic-cyan"
              >
                <Copy size={12} /> Copy
              </button>
              <span className="font-mono text-[10px] uppercase tracking-[0.15em] text-voltaic-muted">
                {referral.uses} referrals · {referral.credits} credit{referral.credits === 1 ? "" : "s"} ready
              </span>
            </div>
            <p className="mt-3 text-xs text-voltaic-muted">
              Friends save 10% with your code. Use it yourself (with this email) to spend a credit on 10% off.
            </p>
          </div>
        </Reveal>
      )}

      {results && results.length > 0 && (
        <div className="mt-8 space-y-5" data-testid="orders-results">
          {results.map((o) => <OrderCard key={o.order_id} order={o} />)}
        </div>
      )}
    </main>
  );
}
